/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { CARDS_ROUTE_PERMISSION } from "@farmlands/card-management";
import { getFeatureRoutePermissionKeys } from "@farmlands/permissions";
import {
  Badge,
  ItemGroup,
  ItemSeparator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@farmlands/shadcn-ui";
import { CreditCard, Receipt, ScrollText, UserRound } from "lucide-react";
import { Fragment } from "react/jsx-runtime";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { useAnyPermission } from "../../../hooks/useAnyPermission";
import { usePermission } from "../../../hooks/usePermission";
import { TestIds } from "../../../testIds";
import { PERSONAL_DETAILS_ROUTES } from "../../account/personal-details";
import { selectIsThisTfo } from "../../azure-b2c/storage";
import { CARDS_ROUTES } from "../../cards/constants";
import { ORDERS_ROUTES } from "../../orders/constants";
import { useQuotes } from "../../quotes/api/hooks/useQuotes";
import { QUOTES_ROUTES } from "../../quotes/constants";

import { QuickLinkTile, QuickLinkTileProps } from "./QuickLinkTile";

interface Tile extends QuickLinkTileProps {
  id: string;
  visible: boolean;
}

export const QuickLinksSection = () => {
  const navigate = useNavigate();

  const canViewQuotes = usePermission("Quotes_QuoteView").allowed;
  const canViewOrders = usePermission("Orders_OrderView").allowed;
  const canManageCards = useAnyPermission(
    getFeatureRoutePermissionKeys(CARDS_ROUTE_PERMISSION)
  ).allowed;

  const isStaff = !!useSelector(selectIsThisTfo);

  const quotesCount = useQuotes(canViewQuotes).data?.length ?? 0;
  const quotesTooltip = isStaff
    ? "The number of quotes your customers have requested"
    : "The number of quotes ready for you to accept";

  const quotesBadge =
    quotesCount > 0 ? (
      <Tooltip>
        <TooltipTrigger asChild>
          <Badge
            className="bg-verdure-electric-lime text-verdure-forest-green"
            data-testid={TestIds.feat.home.quickLinks.quotesBadge}
          >
            {quotesCount}
          </Badge>
        </TooltipTrigger>
        <TooltipContent>{quotesTooltip}</TooltipContent>
      </Tooltip>
    ) : undefined;

  const tiles: Tile[] = [
    {
      id: "quotes",
      visible: canViewQuotes,
      icon: Receipt,
      label: "View quotes",
      onClick: () => navigate(QUOTES_ROUTES.BASE),
      badge: quotesBadge,
      testId: TestIds.feat.home.quickLinks.tile("quotes"),
    },
    {
      id: "orders",
      visible: canViewOrders,
      icon: ScrollText,
      label: "View orders",
      onClick: () => navigate(ORDERS_ROUTES.BASE),
      testId: TestIds.feat.home.quickLinks.tile("orders"),
    },
    {
      id: "card",
      visible: canManageCards,
      icon: CreditCard,
      label: "Report lost or stolen card",
      onClick: () => navigate(CARDS_ROUTES.BASE),
      testId: TestIds.feat.home.quickLinks.tile("card"),
    },
    {
      id: "personal-details",
      visible: true,
      icon: UserRound,
      label: "Update personal details",
      onClick: () => navigate(PERSONAL_DETAILS_ROUTES.BASE),
      testId: TestIds.feat.home.quickLinks.tile("personal-details"),
    },
  ];

  const visibleTiles = tiles.filter((tile) => tile.visible);

  if (visibleTiles.length === 0) {
    return null;
  }

  return (
    <ItemGroup className="gap-1" data-testid={TestIds.feat.home.quickLinks.component}>
      {visibleTiles.map((tile, index) => (
        <Fragment key={tile.id}>
          {index > 0 && <ItemSeparator />}
          <QuickLinkTile {...tile} />
        </Fragment>
      ))}
    </ItemGroup>
  );
};
